import { Alert, Collapse, Stack, Typography } from "@mui/material"
import { ReactElement, useContext } from "react"
import { useForm } from "react-hook-form"

import { useGetUserData } from "../../../hooks/useGetUserData"
import useCreateOrg from "../../../services/middleware/hooks/account/useCreateOrg"
import AppButton from "../../buttons/appButton"
import AppLoadingButton from "../../buttons/appLoadingButton"
import ValidatedTextField from "../../inputs/validated/validatedTextField"
import AddressForm, {
    ADDRESS_KW,
    POSTCODE_KW,
} from "../../submitApplication/single/recognisedInputs/addressForm"
import { BUTTON_SPACING } from "../common/config"
import FullScreenTitleContent from "../common/fullScreenTitleContent"
import { AccountSetUpContext } from "../providers/firstLoginProvider"
import {
    EXISTING_DEVICES_CHECK,
    SELECT_ORG_TYPE,
} from "../screenDispatchers/firstLoginConstants"

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore: Unreachable code error
// eslint-disable-next-line no-undef
const CONFIG = GLOBAL_CONFIG

const CAN_CLAIM_DOMAIN_MSG = (email: string): string =>
    [
        `Any user than signs up with the email ${email} will be automatically routed to your company.`,
        "Any existing user with this address will also be invited to join you.",
    ].join(" ")

const CANNOT_CLAIM_DOMAIN_MSG =
    "You will be able to invite colleagues to join your team."

// Register names
const COMPANY_NAME = "Company Name"
const ORG_PHONE_NUM = "Organisation Phone Number"

interface IOrgDetailsForm {
    manu: boolean
}

/** Filled out when a user is registering their org */
const OrgDetailsForm = ({ manu }: IOrgDetailsForm): ReactElement => {
    const { setCurrentScreen, canClaimDomain } = useContext(AccountSetUpContext)
    const { control, handleSubmit, getValues, setValue } = useForm({
        mode: "all",
        shouldUnregister: true,
    })
    const { isCreating, postCreateOrg, error } = useCreateOrg()
    const { email } = useGetUserData()
    const domain = `@${(email || "").split("@")[1]}`
    const subtitlePlug = manu
        ? "As a manufacturer admin, you can oversee the device registration process"
        : "As an installer admin, you can manage and oversee your team of installers"

    const submitResponse = (): void => {
        const address = getValues(ADDRESS_KW)
        const postCode = getValues(POSTCODE_KW)
        if (!address || !postCode) {
            return
        }
        postCreateOrg(
            getValues(COMPANY_NAME),
            getValues(ADDRESS_KW),
            getValues(POSTCODE_KW),
            getValues(ORG_PHONE_NUM),
            manu ? "manufacturer" : "installer",
            setCurrentScreen
        )
    }

    return (
        <FullScreenTitleContent
            titlePrimaryColor="Let's Register "
            titleMain="Your Company"
            subtitle={`This process sets you up as an admin for your company. ${subtitlePlug}`}
            content={
                <Stack spacing={4}>
                    <Typography
                        variant="h6"
                        color="text.secondary"
                        fontWeight={400}
                    >
                        {canClaimDomain
                            ? CAN_CLAIM_DOMAIN_MSG(domain)
                            : CANNOT_CLAIM_DOMAIN_MSG}
                    </Typography>
                    <ValidatedTextField
                        control={control}
                        label={COMPANY_NAME}
                        registerName={COMPANY_NAME}
                        placeholder="Enter the name of your company..."
                        required
                        disabled={isCreating}
                    />
                    <AddressForm
                        control={control}
                        setValue={setValue}
                        isDefaults={false}
                        worldwide
                        label="Search for your Company's Address Line 1"
                    />
                    <div>
                        <ValidatedTextField
                            control={control}
                            label={ORG_PHONE_NUM}
                            registerName={ORG_PHONE_NUM}
                            placeholder="Enter a contact number for your company..."
                            required
                            disabled={isCreating}
                        />
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            marginTop={1}
                        >
                            This should be a central number that can be
                            contacted for information
                        </Typography>
                    </div>
                </Stack>
            }
            actionContent={
                <Stack width="100%" spacing={BUTTON_SPACING}>
                    <Collapse in={!!error}>
                        <Stack width="100%" spacing={BUTTON_SPACING}>
                            <Alert severity="warning">
                                {error?.displayDetail}
                            </Alert>
                            <AppButton
                                fullWidth
                                variant="outlined"
                                color="warning"
                                onClick={() =>
                                    window.open(CONFIG.SN_PORTAL_URL, "_blank")
                                }
                                disabled={isCreating}
                            >
                                Support Portal
                            </AppButton>
                        </Stack>
                    </Collapse>
                    <AppLoadingButton
                        fullWidth
                        variant="contained"
                        onClick={handleSubmit(submitResponse)}
                        loading={isCreating}
                    >
                        Register My Company
                    </AppLoadingButton>
                    <AppButton
                        fullWidth
                        variant="outlined"
                        onClick={() =>
                            setCurrentScreen(
                                manu ? EXISTING_DEVICES_CHECK : SELECT_ORG_TYPE
                            )
                        }
                        disabled={isCreating}
                    >
                        Back
                    </AppButton>
                </Stack>
            }
        />
    )
}

export default OrgDetailsForm

// TODO - if domain not claimed, what to display
